import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, SendHorizonal } from "lucide-react";
import { useLang } from "@/lib/i18n";

/* Petite broby dessinée pour PAZAPA — currentColor = laine */
function SheepIcon({ className }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
      <ellipse cx="6.5" cy="17" rx="3" ry="4.5" fill="currentColor" transform="rotate(20 6.5 17)" />
      <ellipse cx="25.5" cy="17" rx="3" ry="4.5" fill="currentColor" transform="rotate(-20 25.5 17)" />
      <circle cx="11" cy="9.5" r="4.5" fill="currentColor" />
      <circle cx="16" cy="7.5" r="5" fill="currentColor" />
      <circle cx="21" cy="9.5" r="4.5" fill="currentColor" />
      <circle cx="16" cy="17" r="7.5" fill="currentColor" />
      <circle cx="13" cy="16" r="1.2" fill="hsl(var(--primary))" />
      <circle cx="19" cy="16" r="1.2" fill="hsl(var(--primary))" />
      <path d="M14 20.5 q2 1.8 4 0" stroke="hsl(var(--primary))" strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Rend les **gras** du texte de Broby (au cas où le modèle en utiliserait) */
function renderRichText(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  );
}

export default function BrobyChat() {
  const { t, lang } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("broby_session") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const isAdmin = location.pathname.startsWith("/admin");

  // Petite bulle d'accueil après quelques secondes
  useEffect(() => {
    if (isAdmin) return;
    const timer = setTimeout(() => {
      if (!open && !localStorage.getItem("broby_seen")) setShowBubble(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, [open, isAdmin]);

  // Overture depuis d'autres pages (ex. bouton "Discuter avec Broby")
  useEffect(() => {
    const onOpen = () => openChat();
    window.addEventListener("open-broby", onOpen);
    return () => window.removeEventListener("open-broby", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, lang]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const openChat = async () => {
    setShowBubble(false);
    localStorage.setItem("broby_seen", "1");
    setOpen(true);
    if (messages.length > 0) return;
    if (sessionId) {
      try {
        const tok = localStorage.getItem("broby_token") || "";
        const res = await fetch(`${API}/chat/history/${sessionId}?t=${encodeURIComponent(tok)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.messages?.length) {
            setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
            return;
          }
        }
      } catch {
        /* ignore */
      }
    }
    setMessages([{ role: "assistant", content: t("broby.welcome") }]);
  };

  const send = async (text) => {
    const clean = (text || input).trim();
    if (!clean || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: clean }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: clean, lang }),
      });
      if (res.status === 429) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: t("broby.slow"), isError: true };
          return copy;
        });
        return;
      }
      if (!res.ok || !res.body) throw new Error("chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let gotError = false;

      const appendToken = (token) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + token };
          return copy;
        });
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.token) appendToken(data.token);
            if (data.session_id && data.session_id !== sessionId) {
              setSessionId(data.session_id);
              localStorage.setItem("broby_session", data.session_id);
            }
            if (data.history_token) localStorage.setItem("broby_token", data.history_token);
            if (data.error) gotError = true;
          } catch {
            /* partial json — ignore */
          }
        }
      }
      if (gotError) throw new Error("stream error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("broby.error"), isError: true };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  if (isAdmin) return null;

  const suggestions = ["broby.sug.location", "broby.sug.activities", "broby.sug.inscription"];
  const showSuggestions = messages.length <= 1 && !busy;

  return (
    <>
      {/* Bulle d'accueil */}
      <AnimatePresence>
        {showBubble && !open && (
          <motion.button
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.9 }}
            onClick={openChat}
            data-testid="broby-bubble"
            className="fixed bottom-24 right-4 md:right-8 z-[80] max-w-[240px] text-left bg-white border-2 border-primary/30 rounded-3xl rounded-br-md px-4 py-3 shadow-[0_16px_40px_-12px_rgba(23,49,58,0.35)] text-sm font-semibold text-foreground"
          >
            {t("broby.bubble")}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Bouton flottant */}
      <motion.button
        onClick={() => (open ? setOpen(false) : openChat())}
        data-testid="broby-open-button"
        aria-label={t("broby.open_aria")}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-4 md:bottom-8 right-4 md:right-8 z-[81] w-16 h-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-[0_16px_40px_-10px_rgba(23,49,58,0.45)] border-4 border-background"
      >
        <span className="broby-bob inline-grid place-items-center">
          {open ? <X className="w-7 h-7" strokeWidth={2.4} /> : <SheepIcon className="w-9 h-9" />}
        </span>
      </motion.button>

      {/* Panneau de discussion */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 md:bottom-28 right-4 md:right-8 z-[82] w-[calc(100vw-2rem)] max-w-md bg-background border border-border rounded-3xl shadow-[0_30px_80px_-20px_rgba(23,49,58,0.5)] overflow-hidden flex flex-col"
            style={{ height: "min(560px, calc(100vh - 9rem))" }}
            data-testid="broby-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-primary text-primary-foreground">
              <div className="w-11 h-11 rounded-full bg-white/15 grid place-items-center shrink-0">
                <SheepIcon className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg leading-none">{t("broby.title")}</div>
                <div className="text-xs text-primary-foreground/75 mt-1">{t("broby.subtitle")}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="broby-close"
                aria-label={t("common.close")}
                className="p-2 rounded-full hover:bg-white/15"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="broby-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`broby-message-${m.role}-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : m.isError
                          ? "bg-[#FFE5DE] text-foreground rounded-bl-md"
                          : "bg-muted text-foreground rounded-bl-md"
                    }`}
                  >
                    {renderRichText(m.content)}
                    {m.role === "assistant" && busy && i === messages.length - 1 && !m.content && (
                      <span className="text-foreground/60 italic">{t("broby.thinking")}</span>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {showSuggestions && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {suggestions.map((k) => (
                  <button
                    key={k}
                    onClick={() => send(t(k))}
                    data-testid={`broby-suggestion-${k.split(".").pop()}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {t(k)}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="px-4 py-3 border-t border-border flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("broby.placeholder")}
                data-testid="broby-input"
                maxLength={800}
                className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 ring-primary/40"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="broby-send"
                aria-label={t("broby.send")}
                className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center disabled:opacity-40"
              >
                <SendHorizonal className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </form>
            <p className="px-5 pb-3 text-[10px] text-foreground/50 leading-snug">{t("broby.disclaimer")}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function openBroby() {
  window.dispatchEvent(new CustomEvent("open-broby"));
}
